import {
  Bold,
  CheckSquare,
  Code,
  Eye,
  HelpCircle,
  ImagePlus,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Loader2,
  Pencil,
  Plus,
  Quote,
} from 'lucide-react';
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { CodeMirrorHandle } from '@/components/ui/codemirror';
import { Markdown } from '@/components/ui/markdown';
import { sourceOffsetFromClick } from '@/lib/click-to-source';
import { Input } from '@/components/ui/input';
import {
  appendTaskItems,
  taskItemStats,
  uncheckAllTaskItems,
} from '@/lib/markdown';
import { Menu, MenuContent, MenuTrigger } from '@/components/ui/menu';
import { useToast } from '@/components/ui/toast';
import { isAcceptedFile } from '@/hooks/useAttachments';
import { ApiError } from '@/lib/api/client';
import { attachments as attachmentsApi } from '@/lib/api/endpoints';
import { useT, type MessageKey } from '@/lib/i18n';
import { keys } from '@/lib/queryClient';
import { cn } from '@/lib/utils';

/**
 * CodeMirror is ~190 kB gzipped — three times the rest of the app — and it is
 * needed only once someone actually edits a description. Loading it on demand
 * keeps opening Today as fast as it was before the editor existed.
 */
const MarkdownCodeEditor = lazy(() =>
  import('@/components/ui/codemirror').then((module) => ({
    default: module.MarkdownCodeEditor,
  })),
);

/**
 * The task description: Markdown, in a real editor.
 *
 * Pasting a screenshot while editing uploads it and drops an `![](…)` at the
 * caret, rather than filing it away in the attachment list. That matters when
 * the screenshots tell a story in order — "then this happened, then this" —
 * which a grid of thumbnails cannot express.
 *
 * The upload still creates a normal attachment, so the same image also appears
 * in the attachment list and is governed by the same authorization.
 */
export function DescriptionEditor({
  taskId,
  value,
  onSave,
}: {
  taskId: string;
  value: string | null;
  onSave: (description: string) => void;
}) {
  const t = useT();
  // `null` means "not editing" — see the note on InlineTitle for why the two
  // are one piece of state.
  const [draft, setDraft] = useState<string | null>(null);
  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const [uploading, setUploading] = useState(0);
  const [showsPreview, setShowsPreview] = useState(false);
  const editor = useRef<CodeMirrorHandle | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  const uploadAndInsert = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        if (!isAcceptedFile(file)) {
          toast.error(t('attachment.unsupportedType', { name: file.name }));
          continue;
        }
        setUploading((n) => n + 1);
        try {
          const attachment = await attachmentsApi.upload(taskId, file);
          const url = attachmentsApi.contentUrl(taskId, attachment.id);
          const alt = attachment.original_filename.replace(/[[\]]/g, '');
          const snippet = attachment.mime_type.startsWith('image/')
            ? `![${alt}](${url})`
            : `[${alt}](${url})`;
          // On its own line: an image mid-sentence reads as a mistake, and a
          // sequence of them should stack in the order they were pasted.
          editor.current?.insert(`\n${snippet}\n`);
          void queryClient.invalidateQueries({ queryKey: keys.attachments(taskId) });
          void queryClient.invalidateQueries({ queryKey: keys.task(taskId) });
        } catch (error) {
          toast.error(
            error instanceof ApiError
              ? error.message
              : t('attachment.uploadFailedNamed', { name: file.name }),
          );
        } finally {
          setUploading((n) => n - 1);
        }
      }
    },
    [taskId, queryClient, toast, t],
  );

  /** `text` is the editor's own copy when ⌘S fires; the toolbar button has only the draft. */
  function commit(text?: string) {
    const next = text ?? draft ?? '';
    setDraft(null);
    setShowsPreview(false);
    if (next !== (value ?? '')) onSave(next);
  }

  if (draft === null) {
    return (
      <div>
        <button
          type="button"
          onClick={(event) => {
            setCursor(sourceOffsetFromClick(event, value ?? '') ?? undefined);
            setDraft(value ?? '');
          }}
          className={cn(
            'w-full rounded px-1 py-1 text-left text-sm transition-surface',
            'hover:bg-surface-hover max-sm:py-2 touch:py-2',
            value ? '' : 'text-content-subtle',
          )}
        >
          {value ? (
            <Markdown source={value} onToggleTask={onSave} />
          ) : (
            t('description.add')
          )}
        </button>
        <ChecklistBar value={value} onSave={onSave} />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-accent">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-surface-sunken px-1 py-1">
        {/* The outliner trio first — what Roam does with ⌘↩, Tab and ⇧Tab —
            as buttons, because a phone keyboard has none of those keys, and
            with the to-do spelled out, because as an icon nobody found it. */}
        <ToolbarButton
          icon={CheckSquare}
          label="editor.todo"
          text="editor.todoShort"
          onClick={() => editor.current?.toggleTask()}
        />
        <ToolbarButton
          icon={IndentIncrease}
          label="editor.indent"
          onClick={() => editor.current?.indent()}
        />
        <ToolbarButton
          icon={IndentDecrease}
          label="editor.outdent"
          onClick={() => editor.current?.outdent()}
        />
        <Separator />
        <ToolbarButton
          icon={Bold}
          label="editor.bold"
          onClick={() => editor.current?.wrap('**')}
        />
        <ToolbarButton
          icon={Italic}
          label="editor.italic"
          onClick={() => editor.current?.wrap('*')}
        />
        <ToolbarButton
          icon={Code}
          label="editor.code"
          onClick={() => editor.current?.wrap('`')}
        />
        <Separator />
        <ToolbarButton
          icon={List}
          label="editor.bulletList"
          onClick={() => editor.current?.prefixLines('- ')}
        />
        <ToolbarButton
          icon={ListOrdered}
          label="editor.numberedList"
          onClick={() => editor.current?.prefixLines('1. ')}
        />
        <ToolbarButton
          icon={Quote}
          label="editor.quote"
          onClick={() => editor.current?.prefixLines('> ')}
        />
        <ToolbarButton
          icon={LinkIcon}
          label="editor.link"
          onClick={() => editor.current?.insert('[](https://)')}
        />
        <Separator />
        <ToolbarButton
          icon={ImagePlus}
          label="editor.insertImage"
          accent
          onClick={() => fileInput.current?.click()}
        />

        <span className="flex-1" />

        <MarkdownHelp />
        <ToolbarButton
          icon={showsPreview ? Pencil : Eye}
          label={showsPreview ? 'editor.edit' : 'editor.preview'}
          active={showsPreview}
          onClick={() => setShowsPreview((current) => !current)}
        />
      </div>

      {showsPreview ? (
        <div className="max-h-[26rem] min-h-[8rem] overflow-y-auto bg-surface px-3 py-2">
          {draft.trim() ? (
            <Markdown source={draft} />
          ) : (
            <p className="text-sm text-content-subtle">{t('description.add')}</p>
          )}
        </div>
      ) : (
        <Suspense
          fallback={
            <div className="flex min-h-[8rem] items-center gap-2 px-3 py-2 text-sm text-content-subtle">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {t('common.loading')}
            </div>
          }
        >
          <MarkdownCodeEditor
            initialCursor={cursor}
            value={draft}
            onChange={setDraft}
            onReady={(handle) => {
              editor.current = handle;
            }}
            onPasteFiles={(files) => void uploadAndInsert(files)}
            onSubmit={commit}
            onCancel={() => {
              setDraft(null);
              setShowsPreview(false);
            }}
            placeholder={t('description.placeholder')}
            className="max-h-[26rem] min-h-[8rem] overflow-y-auto bg-surface"
          />
        </Suspense>
      )}

      <input
        ref={fileInput}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp,application/pdf"
        className="sr-only"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = '';
          if (files.length > 0) void uploadAndInsert(files);
        }}
      />

      <div className="flex items-center gap-1.5 border-t border-border bg-surface-sunken px-2 py-1 text-[11px] text-content-subtle">
        {uploading > 0 ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            {t('attachment.uploadingCount', { count: uploading })}
          </>
        ) : (
          <>
            <span className="hidden items-center gap-1.5 sm:flex">
              <kbd className="rounded border border-border px-1">⌘V</kbd>
              {t('description.pasteHint')}
            </span>
            {/* A phone has neither ⌘V nor ⌘↩: name the two ways that exist there. */}
            <span className="min-w-0 sm:hidden">{t('editor.hintTouch')}</span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setShowsPreview(false);
              }}
              className="rounded px-1.5 py-0.5 hover:bg-surface-hover"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => commit()}
              className="flex items-center gap-1 rounded bg-accent px-2 py-0.5 font-medium text-white"
            >
              {t('common.save')}
              <kbd className="rounded border border-white/40 px-1 text-[10px] max-sm:hidden">⌘S</kbd>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Separator() {
  return <span className="mx-1 h-4 w-px bg-border" aria-hidden />;
}

function ToolbarButton({
  icon: Icon,
  label,
  text,
  onClick,
  accent,
  active,
}: {
  icon: typeof Bold;
  /** The tooltip, and the accessible name when there is no visible text. */
  label: MessageKey;
  /** A visible word next to the icon, for the control people must find. */
  text?: MessageKey;
  onClick: () => void;
  accent?: boolean;
  active?: boolean;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      title={t(label)}
      aria-label={text ? undefined : t(label)}
      className={cn(
        // Thumb-sized on phones; the toolbar wraps rather than clipping.
        'flex items-center justify-center gap-1.5 rounded p-1.5 transition-surface hover:bg-surface-hover max-sm:min-h-10 max-sm:min-w-10 touch:min-h-10 touch:min-w-10',
        text && 'px-2 text-xs font-medium ring-1 ring-border',
        accent || active ? 'text-accent' : text ? 'text-content' : 'text-content-muted',
      )}
    >
      <Icon className={cn('h-3.5 w-3.5', text && 'h-4 w-4')} aria-hidden />
      {text ? t(text) : null}
    </button>
  );
}

/** The syntax this app actually renders — no more, so nothing here disappoints. */
const CHEATSHEET: { syntax: string; label: MessageKey }[] = [
  { syntax: '# Nadpis', label: 'editor.help.heading' },
  { syntax: '**text**', label: 'editor.bold' },
  { syntax: '*text*', label: 'editor.italic' },
  { syntax: '`kód`', label: 'editor.code' },
  { syntax: '- položka', label: 'editor.bulletList' },
  { syntax: '1. položka', label: 'editor.numberedList' },
  { syntax: '> citace', label: 'editor.quote' },
  { syntax: '[text](url)', label: 'editor.link' },
  { syntax: '![](url)', label: 'editor.help.image' },
  { syntax: '---', label: 'editor.help.divider' },
  { syntax: '- [ ] úkol', label: 'editor.help.todo' },
  { syntax: '⌘↩', label: 'editor.help.cycle' },
  { syntax: 'Tab / ⇧Tab', label: 'editor.help.indent' },
  { syntax: '/todo …', label: 'editor.help.slash' },
  { syntax: '⌘S', label: 'editor.help.save' },
];

/**
 * The checklist without the editor.
 *
 * A shopping list is the motivating case: items get ticked off, never deleted,
 * and typing them must not require knowing that a checkbox is spelled
 * `- [ ]`. The field appends items to the checklist inside the description —
 * the description stays the single stored value, so the same list renders
 * (and ticks) everywhere a description does.
 */
function ChecklistBar({
  value,
  onSave,
}: {
  value: string | null;
  onSave: (description: string) => void;
}) {
  const t = useT();
  const [item, setItem] = useState('');
  const [starting, setStarting] = useState(false);

  // Quick entry outruns the round trip: Enter, type, Enter again before the
  // refetched description lands. Composing on a local base keeps the second
  // item from being appended to a stale text and silently dropping the first.
  const base = useRef(value);
  useEffect(() => {
    base.current = value;
  }, [value]);

  const { done, total } = taskItemStats(value ?? '');
  if (total === 0 && !starting) {
    return (
      <button
        type="button"
        onClick={() => setStarting(true)}
        className="mt-1 rounded px-1 py-0.5 text-xs text-content-subtle transition-surface hover:text-content max-sm:min-h-11 max-sm:px-2 max-sm:text-sm touch:min-h-11 touch:px-2 touch:text-sm"
      >
        ☑ {t('checklist.start')}
      </button>
    );
  }

  const add = () => {
    // A paste of "mléko\nrohlíky\nmáslo" is a list, not one long item.
    const items = item.split('\n');
    if (items.every((line) => !line.trim())) return;
    const next = appendTaskItems(base.current ?? '', items);
    base.current = next;
    setItem('');
    onSave(next);
  };

  return (
    <div className="mt-1.5">
      {/* Progress first, as a bar: on a phone "2/4" in 11px grey is invisible,
          and the bar is what says at a glance how far along you are. */}
      {total > 0 ? (
        <div className="mb-2 flex items-center gap-3 max-sm:mb-3 touch:mb-3">
          <div
            className="h-1 flex-1 overflow-hidden rounded-full bg-surface-sunken max-sm:h-1.5 touch:h-1.5"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done}
          >
            <div
              className="h-full rounded-full bg-accent transition-[width]"
              style={{ width: `${Math.round((done / total) * 100)}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-content-subtle max-sm:text-[15px] touch:text-[15px]">
            {done}/{total}
          </span>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <form
          className="flex min-w-0 flex-1 items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            add();
          }}
        >
          <Input
            value={item}
            onChange={(event) => setItem(event.target.value)}
            // Enter handled here, not via form submission: the detail dialog
            // has key handlers of its own and implicit submission loses.
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || event.shiftKey) return;
              event.preventDefault();
              event.stopPropagation();
              add();
            }}
            onPaste={(event) => {
              const text = event.clipboardData.getData('text');
              if (!text.includes('\n')) return;
              event.preventDefault();
              const next = appendTaskItems(base.current ?? '', text.split('\n'));
              base.current = next;
              onSave(next);
            }}
            placeholder={t('checklist.addPlaceholder')}
            className="h-7 min-w-0 flex-1 text-sm max-sm:h-12 max-sm:text-[17px] touch:h-12 touch:text-[17px]"
            autoFocus={starting}
            autoComplete="off"
          />
          {/* A visible way to add on a phone, where "press Enter" is a soft
              keyboard button people do not look for. */}
          <button
            type="submit"
            disabled={!item.trim()}
            aria-label={t('common.add')}
            className={cn(
              'hidden shrink-0 items-center justify-center rounded-lg bg-accent text-accent-content',
              'disabled:opacity-40 max-sm:flex max-sm:h-12 max-sm:w-12 touch:flex touch:h-12 touch:w-12',
            )}
          >
            <Plus className="h-5 w-5" aria-hidden />
          </button>
        </form>
        {done > 0 ? (
          <button
            type="button"
            onClick={() => {
              const next = uncheckAllTaskItems(base.current ?? '');
              base.current = next;
              onSave(next);
            }}
            className="text-xs text-content-subtle underline-offset-2 transition-surface hover:text-content hover:underline max-sm:min-h-11 max-sm:px-2 max-sm:text-sm touch:min-h-11 touch:px-2 touch:text-sm"
          >
            {t('checklist.uncheckAll')}
          </button>
        ) : null}
      </div>
    </div>
  );
}


function MarkdownHelp() {
  const t = useT();
  return (
    <Menu>
      <MenuTrigger
        className="flex items-center justify-center rounded p-1.5 text-content-muted transition-surface hover:bg-surface-hover max-sm:min-h-10 max-sm:min-w-10 touch:min-h-10 touch:min-w-10"
        aria-label={t('editor.help')}
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden />
      </MenuTrigger>
      <MenuContent align="end" className="w-64 p-2">
        <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-subtle">
          {t('editor.help')}
        </p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 px-1">
          {CHEATSHEET.map(({ syntax, label }) => (
            <div key={syntax} className="contents">
              <dt>
                <code className="rounded bg-surface-sunken px-1 py-0.5 text-[11px] text-content">
                  {syntax}
                </code>
              </dt>
              <dd className="self-center text-[11px] text-content-muted">{t(label)}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-2 border-t border-border px-1 pt-1.5 text-[11px] text-content-subtle">
          {t('editor.help.autocomplete')}
        </p>
      </MenuContent>
    </Menu>
  );
}
